import { Link, router } from "@inertiajs/react"
import { MapPin } from "lucide-react"
import { type ReactNode } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { AdminLayout } from "@/layouts/AdminLayout"
import type { ContentStatus, PaginationMeta } from "@/lib/types"

interface PoiItem {
  id: number
  name: string
  categories: { id: number; name: string }[]
  status: ContentStatus
  updated_at: string | null
  edit_url: string
}

interface Props {
  region: { id: number; name: string }
  pois: PoiItem[]
  meta: PaginationMeta
  urls: {
    index: string
    region: string
    new_poi: string
  }
}

function statusVariant(status: ContentStatus): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "approved":      return "default"
    case "rejected":      return "destructive"
    case "pending":       return "secondary"
    case "needs_changes": return "secondary"
    case "draft":         return "outline"
    case "archived":      return "outline"
    default:              return "outline"
  }
}

function PaginationControls({ meta, indexUrl }: { meta: PaginationMeta; indexUrl: string }) {
  if (meta.pages <= 1) return null

  return (
    <div className="flex items-center justify-end gap-2 py-3 text-sm text-muted-foreground">
      {meta.page > 1 && (
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            router.get(indexUrl, { page: meta.page - 1 }, { preserveState: true })
          }
        >
          Previous
        </Button>
      )}
      <span className="flex items-center px-2">
        Page {meta.page} of {meta.pages}
      </span>
      {meta.page < meta.pages && (
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            router.get(indexUrl, { page: meta.page + 1 }, { preserveState: true })
          }
        >
          Next
        </Button>
      )}
    </div>
  )
}

export default function RegionPoisIndex({ region, pois, meta, urls }: Props) {
  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="mb-1 flex items-center gap-2 text-sm text-muted-foreground">
            <Link href={urls.region} className="hover:underline">
              {region.name}
            </Link>
            <span aria-hidden="true">/</span>
            <span>POIs</span>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            POIs — {region.name}
          </h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {meta.count} POI{meta.count !== 1 ? "s" : ""} · Public points of interest curated for this region.
          </p>
        </div>
        <Button asChild size="sm">
          <Link href={urls.new_poi}>Add POI</Link>
        </Button>
      </div>

      {pois.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border py-20 text-center">
          <MapPin className="mb-4 h-10 w-10 text-muted-foreground opacity-30" aria-hidden="true" />
          <p className="text-sm font-medium text-muted-foreground">
            No POIs yet for {region.name}.
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Start curating local recommendations for guests.
          </p>
          <Button asChild size="sm" className="mt-4">
            <Link href={urls.new_poi}>Add your first local recommendation</Link>
          </Button>
        </div>
      ) : (
        <>
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Categories</TableHead>
                  <TableHead>Last Updated</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pois.map((poi) => (
                  <TableRow key={poi.id}>
                    <TableCell className="font-medium">{poi.name}</TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(poi.status)} className="capitalize">
                        {poi.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {poi.categories.length === 0 ? (
                          <span className="text-xs text-muted-foreground italic">—</span>
                        ) : (
                          poi.categories.map((cat) => (
                            <Badge key={cat.id} variant="outline" className="text-xs">
                              {cat.name}
                            </Badge>
                          ))
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {poi.updated_at
                        ? new Date(poi.updated_at).toLocaleDateString()
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <Link
                        href={poi.edit_url}
                        className="text-sm text-primary hover:underline"
                      >
                        Edit
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <PaginationControls meta={meta} indexUrl={urls.index} />
        </>
      )}
    </div>
  )
}

RegionPoisIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
